/**
 * In a MedicAI Connect chat, a patient the practice links (`#`) that Connect does not have yet: the sender is asked
 * whether to send them, which submits the patient (and their ICD codes, never procedure codes) to Connect.
 *
 * Keyed by the message the patient was linked in. A patient Connect has, or a practice not on Connect, asks nothing.
 */
export type ConnectPromptState = 'checking' | 'ask' | 'sending' | 'sent' | 'error';

export interface ConnectPatientPrompt {
  patientRef: string;
  title: string;
  state: ConnectPromptState;
}

export interface ConnectPatientApi {
  status(patientRef: string): Promise<{ connectLinked: boolean; onConnect: boolean }>;
  send(patientRef: string): Promise<unknown>;
}

export class ConnectPatientPrompts {
  private readonly byMessage = new Map<string, ConnectPatientPrompt[]>();
  /** what failed, so Try again repeats it */
  private readonly failed = new Map<string, 'check' | 'send'>();

  constructor(private readonly api: ConnectPatientApi) {}

  of(messageId: string): ConnectPatientPrompt[] {
    return this.byMessage.get(messageId) ?? [];
  }

  /** Checks each patient linked in one message, once. */
  check(messageId: string, patients: readonly { patientRef: string; title: string }[]): void {
    const existing = this.of(messageId);
    const fresh = patients.filter((p) => !existing.some((e) => e.patientRef === p.patientRef));
    if (!fresh.length) return;
    this.byMessage.set(messageId, [...existing, ...fresh.map((p) => ({ patientRef: p.patientRef, title: p.title, state: 'checking' as const }))]);
    for (const p of fresh) void this.runCheck(messageId, p.patientRef);
  }

  send(messageId: string, patientRef: string): void {
    this.set(messageId, patientRef, 'sending');
    this.api.send(patientRef).then(
      () => this.set(messageId, patientRef, 'sent'),
      () => this.fail(messageId, patientRef, 'send'),
    );
  }

  retry(messageId: string, patientRef: string): void {
    if (this.failed.get(key(messageId, patientRef)) === 'send') this.send(messageId, patientRef);
    else {
      this.set(messageId, patientRef, 'checking');
      void this.runCheck(messageId, patientRef);
    }
  }

  dismiss(messageId: string, patientRef: string): void {
    const left = this.of(messageId).filter((p) => p.patientRef !== patientRef);
    if (left.length) this.byMessage.set(messageId, left);
    else this.byMessage.delete(messageId);
  }

  clear(): void {
    this.byMessage.clear();
    this.failed.clear();
  }

  private async runCheck(messageId: string, patientRef: string): Promise<void> {
    try {
      const s = await this.api.status(patientRef);
      if (!s.connectLinked || s.onConnect) this.dismiss(messageId, patientRef);
      else this.set(messageId, patientRef, 'ask');
    } catch {
      this.fail(messageId, patientRef, 'check');
    }
  }

  private fail(messageId: string, patientRef: string, what: 'check' | 'send'): void {
    this.failed.set(key(messageId, patientRef), what);
    this.set(messageId, patientRef, 'error');
  }

  private set(messageId: string, patientRef: string, state: ConnectPromptState): void {
    const list = this.byMessage.get(messageId);
    if (!list) return;
    this.byMessage.set(messageId, list.map((p) => (p.patientRef === patientRef ? { ...p, state } : p)));
  }
}

const key = (messageId: string, patientRef: string) => `${messageId}:${patientRef}`;
